import React, { useState, useEffect } from "react";
import { ref, onValue } from "firebase/database";
import { db } from "./firebase"; // Adjust the path to your firebase setup
import { Box, Grid } from "@mui/material";
import SimplePageHeader from "./Components/Navigation/SimplePageHeader";
import DeploymentsPerCompanyChart from "./Components/Charts/DeploymentsPerCompanyChart";
import DeploymentsPerMonthChart from "./Components/Charts/DeploymentsPerMonthChart";
import DeploymentCompletionStatusChart from "./Components/Charts/DeploymentCompletionStatusChart";
import ApplicationInstallationStatusChart from "./Components/Charts/ApplicationInstallationStatusChart";
import TopFailedApplicationsChart from "./Components/Charts/TopFailedApplicationsChart";
import MostPopularApplicationsChart from "./Components/Charts/MostPopularApplicationsChart";

function StatisticsPage() {
  const [deployments, setDeployments] = useState([]);
  const [archivedDeployments, setArchivedDeployments] = useState([]);
  const [deploymentLogs, setDeploymentLogs] = useState({});
  const [companyApplications, setCompanyApplications] = useState({});

  useEffect(() => {
    // Fetch active deployments
    const deploymentsRef = ref(db, "Deployments");
    onValue(deploymentsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setDeployments(Object.values(data));
    });

    // Fetch archived deployments
    const archiveRef = ref(db, "Archive");
    onValue(archiveRef, (snapshot) => {
      const data = snapshot.val() || {};
      setArchivedDeployments(Object.values(data));
    });

    // Fetch deployment logs
    const deploymentLogsRef = ref(db, "deploymentLogs");
    onValue(deploymentLogsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setDeploymentLogs(data);
    });

    // Fetch company applications
    const companyApplicationsRef = ref(db, "CompanyApplications");
    onValue(companyApplicationsRef, (snapshot) => {
      const data = snapshot.val() || {};
      setCompanyApplications(data);
    });
  }, []);

  // Combine active and archived deployments
  const allDeployments = [...deployments, ...archivedDeployments];

  // Helper function to aggregate data from deployment logs
  const getApplicationStats = (logs) => {
    let successCount = 0;
    let failureCount = 0;
    const failureReasons = {};

    for (const company of Object.values(logs)) {
      for (const machine of Object.values(company)) {
        for (const log of Object.values(machine)) {
          // Check if log.applications exists and is an object
          const applications = log.applications || {};

          for (const [app, status] of Object.entries(applications)) {
            if (status === "Installed") {
              successCount++;
            } else if (status === "Failed") {
              failureCount++;
              failureReasons[app] = (failureReasons[app] || 0) + 1;
            }
          }
        }
      }
    }
    return { successCount, failureCount, failureReasons };
  };

  const getMostPopularApplications = (companyApps) => {
    const appCounts = {};
    for (const company in companyApps) {
      const apps = companyApps[company] || {};
      for (const app in apps) {
        if (apps[app]) {
          appCounts[app] = (appCounts[app] || 0) + 1;
        }
      }
    }
    return Object.keys(appCounts).map((app) => ({
      name: app,
      count: appCounts[app],
    }));
  };

  // Deployment statistics
  const deploymentsPerCompany = allDeployments.reduce((acc, deployment) => {
    const companyName = deployment.companyName || "Unknown Company";
    acc[companyName] = (acc[companyName] || 0) + 1;
    return acc;
  }, {});

  const deploymentsPerMonth = allDeployments.reduce((acc, deployment) => {
    const startTime = deployment.startTime || "";
    const month = startTime
      ? new Date(startTime).toLocaleString("default", {
          month: "short",
          year: "numeric",
        })
      : "Unknown Month";
    acc[month] = (acc[month] || 0) + 1;
    return acc;
  }, {});

  const completionStatus = {
    completed: allDeployments.filter(
      (deployment) => deployment.percentComplete === 100,
    ).length,
    inProgress: allDeployments.filter(
      (deployment) => deployment.percentComplete < 100,
    ).length,
  };

  // Convert deployment data to chart format
  const companyChartData = Object.keys(deploymentsPerCompany).map(
    (companyName) => ({
      name: companyName,
      deployments: deploymentsPerCompany[companyName],
    }),
  );

  const monthChartData = Object.keys(deploymentsPerMonth).map((month) => ({
    name: month,
    deployments: deploymentsPerMonth[month],
  }));

  const completionData = [
    { name: "Completed", value: completionStatus.completed },
    { name: "In Progress", value: completionStatus.inProgress },
  ];

  // Deployment logs statistics
  const { successCount, failureCount, failureReasons } =
    getApplicationStats(deploymentLogs);

  // Convert deployment logs data to chart format
  const topFailedApplicationsData = Object.keys(failureReasons).map((app) => ({
    name: app,
    count: failureReasons[app],
  }));

  // Convert company applications data to chart format
  const mostPopularAppsData = getMostPopularApplications(companyApplications);

  return (
    <>
      <SimplePageHeader
        iconName="statistics"
        showAddButton={false}
        titleString={"Deployment Statistics"}
      />
      <Box sx={{ padding: 2 }}>
        <Grid container spacing={3}>
          {/* Deployments per Company */}
          <Grid item xs={12} md={6}>
            <DeploymentsPerCompanyChart data={companyChartData} />
          </Grid>

          {/* Most Popular Applications */}
          <Grid item xs={12} md={6}>
            <MostPopularApplicationsChart data={mostPopularAppsData} />
          </Grid>

          {/* Deployments per Month */}
          <Grid item xs={12} md={6}>
            <DeploymentsPerMonthChart data={monthChartData} />
          </Grid>

          {/* Deployment Completion Status */}
          <Grid item xs={12} md={6}>
            <DeploymentCompletionStatusChart data={completionData} />
          </Grid>

          {/* Application Installation Status */}
          <Grid item xs={12} md={6}>
            <ApplicationInstallationStatusChart
              data={[
                { name: "Installed", value: successCount },
                { name: "Failed", value: failureCount },
              ]}
            />
          </Grid>

          {/* Top Failed Applications */}
          <Grid item xs={12} md={6}>
            <TopFailedApplicationsChart data={topFailedApplicationsData} />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default StatisticsPage;
